export * from './Landlord';
export * from './Listing';
export * from './Building';
export * from './Company';
export * from './Store';
export * from './Property';
export * from './Search';
export * from './Forms';
export * from './Tourbook';
export * from './Map';

export type CountryCode = 'US' | 'GB';
export type AbbreviatedRegion = 'NY' | 'CA' | 'IL' | 'DC' | 'MA' | 'UK';
export type Locale = 'en-us' | 'en-gb';
export type LocaleUpCase = 'en-US' | 'en-GB';
export type Currency = 'USD' | 'GBP';
export type CurrencyWithSentenceCase = 'USD' | 'GBP' | 'Usd' | 'Gbp';
export type Units = 'imperial' | 'metric';
export type LengthUnit = 'Inches' | 'Centimeters';
export type AreaUnit = 'SquareFoot' | 'SquareMeter';
export type MarketSlug =
  | 'boston'
  | 'new_york_city'
  | 'chicago'
  | 'london'
  | 'los_angeles_area'
  | 'sf_bay_area'
  | 'washington_dc';

export type Image = {
  path: string;
  smallPath: string;
  mediumPath: string;
  largePath: string;
  rawPath: string;
  description: string;
  cloudinaryId: string;
};

type VideoTransformation = [string, string] | string;
export type Video = {
  posterPath: string;
  thumbnailPosterPath: string;
  description: string;
  cloudinaryId: string;
  sourceTypes: ['mp4'] | ['hls', 'mp4'];
  sourceTransformation: { hls?: VideoTransformation[]; mp4: VideoTransformation[] };
  posterStartOffset: string | null;
};

export type Area = {
  magnitude: number;
  unit: Unit;
};

export type MinMaxArea = {
  min: Area | null | undefined;
  max: Area | null | undefined;
  unit: Unit;
};

export type Unit = 'SquareFoot' | 'SquareMeter';

export type UserRole =
  | 'unknown'
  | 'agency_broker'
  | 'agency_and_tenant_rep_broker'
  | 'landlord'
  | 'tenant_rep_broker'
  | 'tenant_rep'
  | 'admin';

export type UnchangableUserRole = 'admin' | 'tenant_rep' | 'unknown';

export type ChangeableUserRole = Exclude<UserRole, UnchangableUserRole>;

export type UserAnalyticsInformation = {
  userId: string;
  userEmail: string;
  userFirstName: string;
  userLastName: string;
  userTitle: string | null;
  userPhone: string | null;
  userMarket: string;
  userAccountType: string;
  initialSmsOptIn: boolean;
  vtsEmployerId: number | null;
  marketplaceUserId: string;
  vtsUserId: number;
};

export type CurrentUser = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  jobTitle: string | null;
  languagePreference: Locale | null;
  role: UserRole;
  marketSlug: string;
  phoneNumber: string | null;
  formattedPhoneNumber: string | null;
  tourbookPhoneNumber: string | null;
  formattedTourbookPhoneNumber: string | null;
  marketName: string;
  marketCountryCode: CountryCode;
  analyticsInformation: UserAnalyticsInformation;
  tourbookAvatarCloudinaryId: string;
  tourbookAvatarImage?: Image;
  tourbookCompanyLogoCloudinaryId: string;
  tourbookCompanyImage?: Image;
  userContentAgreement: boolean;
};

export type ValidSubwayLine =
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | 'A'
  | 'B'
  | 'C'
  | 'D'
  | 'E'
  | 'F'
  | 'G'
  | 'J'
  | 'L'
  | 'M'
  | 'N'
  | 'Q'
  | 'R'
  | 'S'
  | 'SIR'
  | 'W'
  | 'Z';
export type NearbyStop = {
  distanceInMiles: number;
  stopName: string;
  lines: ValidSubwayLine[];
};

export type AgentContact = {
  name: string;
  email: string;
  phone: string | null;
  company: string | null;
  role: string | null;
  id: string;
  photoUrl: string | null;
  sortableId?: string;
};

export type PaginationResponse = {
  currentPage: number;
  totalCount: number;
  pageSize: number;
};

export type Pagination = PaginationResponse & {
  ids: string[];
};

export type DeprecatedErrorResponse = {
  messages: string[];
  invalidFields: string[];
};

type TODO_TYPE_IT = any;
export type Error = { field: string | null; message: string; metadata?: TODO_TYPE_IT };
export type ErrorResponse = { errors: Error[] };

export type BackendEnums = { markets: Market[] };
export type TruvaConfig = {
  translationsJSON: string;
  locale: 'en-us';
  enums: BackendEnums;
};

export type Coordinate = { lng: number; lat: number };
export type Shape = {
  id: string;
  name: string;
  slug: string;
  abbreviatedRegion: string;
  locality: string;
  boundaries: Array<Coordinate[]>;
  envelope: [Coordinate, Coordinate, Coordinate, Coordinate];
};

export type InquiryFormState = 'initial' | 'sending' | 'success' | 'failure';

export type DateString = string;

export type MediaStatus = 'pending' | 'successful' | 'failed';

export type AdminMedia = {
  filename: string;
  status: MediaStatus;
};

export type Market = {
  shapeSlug: string;
  isPublic: boolean;
  id: string;
  countryCode: string;
};

export type Breadcrumb =
  | string
  | {
      content: string;
      link?: string;
      onClick?: () => void;
    }
  | null;
export type Breadcrumbs = Breadcrumb[];

export type IterableUserAttributes = {
  iterableEmailCampaignId?: string;
  iterableTemplateId?: string;
  iterableMessageId?: string;
  iterableEndUserId?: string;
};

export type ListingSearchFilterTypes = 'mobile' | 'quick' | 'all';

export type FirstPhotoOrientation = 'landscape' | 'portrait';

export type AdminDataSource<T> = {
  value: T | null;
  dataSourceId: string | null;
  dataSource: string | null;
};

export type IntersectionObserver = {
  isSticky: boolean;
  intersectionRatio: number | undefined;
};

export type Brochure = {
  name: string;
  url: string;
  id: string;
};

/**
 * This is a generic convertable object, should only be used
 * when we have no idea what kind of convertable object
 * (prefer specific ones)
 * */
export type ConvertableGeneric = {
  magnitude: number;
  type: string;
  units: {
    area?: string;
    period?: string;
    money?: string;
  };
};

/** This is a convertable time period, day, year or month */
export type ConvertablePeriod = {
  magnitude: number;
  type: 'period';
  units: {
    period: 'day' | 'mo' | 'yr';
  };
};

export type ConvertableMoneyPerPeriod = {
  magnitude: number;
  type: 'money/period';
  units: {
    money: Currency;
    period: 'yr' | 'mo' | 'day';
  };
};

export type ConvertableMoneyPerAreaPerPeriod = {
  magnitude?: number;
  type: 'money/area/period';
  units: ConvertableMoneyPerAreaPerPeriodUnits;
};

export type ConvertableMoneyPerAreaPerPeriodUnits = {
  area: 'SquareFoot' | 'SquareMeter' | 'sf' | 'sm';
  money: Currency;
  period: 'year' | 'month' | 'day';
};

/** This is a convertable area, sf will be there for
 * areas wrapped in MeasureSerializer, SquareFoot for ones
 * that aren't
 */
export type ConvertableArea = {
  magnitude: number;
  type: 'area';
  units: {
    area: 'SquareFoot' | 'SquareMeter' | 'sf' | 'sm';
  };
};

export type ConvertableLength = {
  magnitude: number;
  type: 'length';
  units: {
    length: 'Inches' | 'Centimeters';
  };
};

export type Address = {
  buildingName: string | null;
  street: string;
  city: string;
  countryCode: string;
  postalCode: string;
  abbreviatedRegion: string;
};

export type VirtualTourSpotlights = 'unset' | 'three_d_tour' | 'video';

export type AdminVideo = {
  id: string;
  path: string;
  filename: string | null;
  description: string | null;
  thumbnailPosterPath: string;
  posterStartOffset: number | null;
  processedAt: string | null;
};

export type Pages =
  | 'listingSearch'
  | 'myTourbooks'
  | 'tourbookExternalListing'
  | 'tourbookShow'
  | 'savedSearches'
  | 'savedListings'
  | 'savedBuildingOwners'
  | 'savedBuildings'
  | 'userAccountSettings';

export type Submarket = {
  abbreviatedRegion: string;
  boundaries: Coordinate[][];
  envelope: Coordinate;
  id: string;
  locality: string;
  name: string;
  slug: string;
};

export type Breakpoint = 'mobile' | 'tablet' | 'desktop';
